import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";

// Slice locali
import {
    fetchFasiFrazioniReparto,
    FaseFrazioneReparto,
    FasiFrazioniRepartoList,
    setFaseFrazioneRepartoCorrenteId,
    resetFasiFrazioniReparto,
} from "./fasiFrazioniRepartoSlice";

// Slice condivisi
import { fetchReparti, Reparto, RepartiList } from "components/anagrafiche/reparti/repartiSlice";
import {
    FiltriFasiFrazioniReparto,
    setFiltriFasiFrazioniReparto,
} from "components/userPreference/userPreferenceSlice";
import { ErrorStatusTypes } from "components/common/errorsDeclarations";
import { convertToDateTime } from "../../common/dateUtils/convertToDateTime";
import { DateTime } from "luxon";

// Auth
import { useAutorizzazioneGruppi } from "../../authentication/useAutorizzazioneGruppi";
import { PermissionTypes } from "../../authentication/permissionsGroups";

// Componente dettaglio
import FaseFrazioneRepartoDetailDialog from "./FaseFrazioneRepartoDetailDialog";
import OperatoreSelectInlineFaseFrazione from "./OperatoreSelectInlineFaseFrazione";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { theme } from "../../theme";

// ============================================
// COMPONENT
// ============================================

const FasiFrazioniRepartoPage = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const fasiFrazioniList = useSelector<RootState, FasiFrazioniRepartoList>(
        (state) => state.fasiFrazioniRepartoState.fasiFrazioniRepartoList
    );

    const faseFrazioneCorrenteId = useSelector<RootState, number | undefined>(
        (state) => state.fasiFrazioniRepartoState.faseFrazioneRepartoCorrenteId
    );

    const repartiList = useSelector<RootState, RepartiList>(
        (state) => state.repartiState.repartiList
    );

    const filtri = useSelector<RootState, FiltriFasiFrazioniReparto>(
        (state) => state.userPreferenceState.filtriFasiFrazioniReparto || {}
    );

    const faseFrazioneCorrente = fasiFrazioniList.results.find(
        (f) => f.id === faseFrazioneCorrenteId
    );

    const consentiModificaOperatore = useAutorizzazioneGruppi([
        PermissionTypes.PIANIFICATORI,
    ]);

    // ============================================
    // STATI LOCALI
    // ============================================

    const [searchValue, setSearchValue] = useState(filtri?.search || "");
    const [refresh, setRefresh] = useState(false);
    const [soloOstacoli, setSoloOstacoli] = useState<boolean>(
        filtri?.solo_ostacoli || false
    );
    const [repartoSelezionato, setRepartoSelezionato] = useState<Reparto | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // ============================================
    // EFFECTS
    // ============================================

    // Carica reparti all'avvio
    useEffect(() => {
        if (repartiList.results.length === 0) {
            dispatch(fetchReparti());
        }
    }, []);

    // Se c'è un reparto nei filtri persistenti, selezionalo
    useEffect(() => {
        if (filtri.reparto_id && repartiList.results.length > 0 && !repartoSelezionato) {
            const reparto = repartiList.results.find((r) => r.id === filtri.reparto_id);
            if (reparto) {
                setRepartoSelezionato(reparto);
            }
        }
    }, [repartiList.results]);

    // Fetch fasi quando cambia reparto selezionato
    useEffect(() => {
        if (repartoSelezionato?.id) {
            const filtriIniziali: FiltriFasiFrazioniReparto = {
                ...filtri,
                reparto_id: repartoSelezionato.id,
                numero_record: filtri.numero_record || 20,
                page: filtri.page || 1,
            };
            dispatch(setFiltriFasiFrazioniReparto(filtriIniziali));
            dispatch(fetchFasiFrazioniReparto({
                repartoId: repartoSelezionato.id,
                filtri: filtriIniziali,
            }));
        } else {
            dispatch(resetFasiFrazioniReparto());
        }
    }, [repartoSelezionato?.id]);

    // Refresh con flag
    useEffect(() => {
        if (refresh && repartoSelezionato?.id) {
            const nuoviFiltri: FiltriFasiFrazioniReparto = {
                ...filtri,
                reparto_id: repartoSelezionato.id,
                search: searchValue || undefined,
                solo_ostacoli: soloOstacoli || undefined,
                page: 1,
            };
            dispatch(setFiltriFasiFrazioniReparto(nuoviFiltri));
            dispatch(fetchFasiFrazioniReparto({
                repartoId: repartoSelezionato.id,
                filtri: nuoviFiltri,
            }));
            setRefresh(false);
        }
    }, [refresh]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleRepartoChange = (_event: React.SyntheticEvent, value: Reparto | null) => {
        setRepartoSelezionato(value);
        setSearchValue("");
        setSoloOstacoli(false);
    };

    const handleResetFiltri = () => {
        setSearchValue("");
        setSoloOstacoli(false);

        if (repartoSelezionato?.id) {
            const filtriDefault: FiltriFasiFrazioniReparto = {
                reparto_id: repartoSelezionato.id,
                numero_record: 20,
                page: 1,
            };
            dispatch(setFiltriFasiFrazioniReparto(filtriDefault));
            dispatch(fetchFasiFrazioniReparto({
                repartoId: repartoSelezionato.id,
                filtri: filtriDefault,
            }));
        }
    };

    const handlePaginationChange = (
        _event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        if (!repartoSelezionato?.id) return;
        const nuoviFiltri = { ...filtri, page };
        dispatch(setFiltriFasiFrazioniReparto(nuoviFiltri));
        dispatch(fetchFasiFrazioniReparto({
            repartoId: repartoSelezionato.id,
            filtri: nuoviFiltri,
        }));
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        if (!repartoSelezionato?.id) return;
        const numero_record = event.target.value as number;
        const nuoviFiltri: FiltriFasiFrazioniReparto = {
            ...filtri,
            numero_record,
            page: 1,
        };
        dispatch(setFiltriFasiFrazioniReparto(nuoviFiltri));
        dispatch(fetchFasiFrazioniReparto({
            repartoId: repartoSelezionato.id,
            filtri: nuoviFiltri,
        }));
    };

    const handleClickFase = (fase: FaseFrazioneReparto) => {
        dispatch(setFaseFrazioneRepartoCorrenteId(fase.id));
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch(setFaseFrazioneRepartoCorrenteId(undefined));
    };

    // ============================================
    // HELPERS
    // ============================================

    const formatOre = (ore?: string) => {
        if (!ore) return "-";
        const num = parseFloat(ore);
        if (isNaN(num)) return "-";
        const h = Math.floor(num);
        const m = Math.round((num - h) * 60);
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };

    const getAvanzamento = (fase: FaseFrazioneReparto): number => {
        const val = parseFloat(fase.percentuale_avanzamento || "0");
        return isNaN(val) ? 0 : val;
    };

    // ============================================
    // RENDER
    // ============================================

    const repartiAttivi = repartiList.results.filter((r) => r.attivo !== false);

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            {/* Header */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Fasi per Reparto
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        options={repartiAttivi}
                        getOptionLabel={(option) =>
                            option.codice
                                ? `${option.codice} - ${option.descrizione}`
                                : option.descrizione || ""
                        }
                        value={repartoSelezionato}
                        onChange={handleRepartoChange}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Seleziona Reparto"
                                size="small"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    {repartoSelezionato && (
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ justifyContent: "flex-end" }}
                        >
                            <FormControl
                                sx={{ minWidth: 200, width: "100%" }}
                                variant="outlined"
                                size="small"
                            >
                                <InputLabel>Ricerca</InputLabel>
                                <OutlinedInput
                                    type="text"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setRefresh(true)}
                                                edge="end"
                                                size="small"
                                            >
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Ricerca"
                                    placeholder="Fase, articolo, OdP, cliente..."
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onBlur={() => setRefresh(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setRefresh(true);
                                        }
                                    }}
                                />
                            </FormControl>
                        </Stack>
                    )}
                </Grid>
            </Grid>

            {/* Filtri */}
            {repartoSelezionato && (
                <Card sx={{ mb: theme.spacing(2), p: theme.spacing(1.5) }}>
                    <Grid container spacing={2} sx={{ alignItems: "center" }}>
                        <Grid size="auto">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={soloOstacoli}
                                        onChange={(e) => {
                                            setSoloOstacoli(e.target.checked);
                                            setRefresh(true);
                                        }}
                                        size="small"
                                    />
                                }
                                label={
                                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                        <WarningAmberIcon fontSize="small" color="warning" />
                                        <Typography variant="body2" fontWeight="bold">
                                            Solo con ostacoli
                                        </Typography>
                                    </Stack>
                                }
                                sx={{
                                    backgroundColor: soloOstacoli
                                        ? theme.palette.action.selected
                                        : "inherit",
                                    borderRadius: 1,
                                    px: 1,
                                }}
                            />
                        </Grid>

                        <Grid size="auto">
                            <Typography variant="body2" color="text.secondary">
                                {fasiFrazioniList.count} fasi trovate
                            </Typography>
                        </Grid>

                        <Grid size="auto" sx={{ ml: "auto" }}>
                            <IconButton onClick={handleResetFiltri} color="primary">
                                <FilterAltOffIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Card>
            )}

            {/* Stato: nessun reparto selezionato */}
            {!repartoSelezionato && (
                <Card>
                    <CardContent>
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <PrecisionManufacturingIcon
                                sx={{ fontSize: 80, color: "grey.300", mb: 2 }}
                            />
                            <Typography variant="h6" color="text.secondary">
                                Seleziona un reparto per visualizzare le fasi schedulate
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Lista Fasi */}
            {repartoSelezionato && (
                <Box>
                    {fasiFrazioniList.results.map((fase: FaseFrazioneReparto) => {
                        const avanzamento = getAvanzamento(fase);
                        const hasOstacoli = fase.ha_ostacoli_attivi;

                        return (
                            <Card
                                key={fase.id}
                                sx={{
                                    mb: theme.spacing(1),
                                    cursor: "pointer",
                                    border: hasOstacoli
                                        ? `2px solid ${theme.palette.warning.main}`
                                        : fase.is_bloccata
                                            ? `2px solid ${theme.palette.error.main}`
                                            : `1px solid ${theme.palette.divider}`,
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    // Touch-friendly padding per tablet
                                    minHeight: 72,
                                }}
                                onClick={() => handleClickFase(fase)}
                            >
                                <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, "&:last-child": { pb: 1.5 } }}>
                                    <Grid container spacing={1.5} sx={{ alignItems: "center" }}>
                                        {/* OdP + Articolo */}
                                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                OdP {fase.numero_registrazione}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {fase.codice_articolo}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="text.secondary">
                                                {fase.nome_cliente}
                                            </Typography>
                                        </Grid>

                                        {/* Descrizione lavorazione */}
                                        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                                            <Typography variant="body2">
                                                {fase.descrizione} ({fase.codice_lavorazione})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {fase.note}
                                            </Typography>
                                        </Grid>

                                        {/* Operatore (select inline) */}
                                        <Grid size={{ xs: 12, sm: 12, md: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Operatore
                                            </Typography>
                                            <OperatoreSelectInlineFaseFrazione
                                                fase={fase}
                                                consentiModifica={consentiModificaOperatore}
                                            />
                                        </Grid>

                                        {/* Quantità e Ore */}
                                        <Grid size={{ xs: 6, sm: 2, md: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Qtà schedulata
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {fase.quantita} {fase.unita_misura}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mt: 0.5 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatOre(fase.ore_previste)} prev. / {formatOre(fase.ore_residue)} res.
                                                </Typography>
                                            </Stack>
                                        </Grid>

                                        {/* Date */}
                                        <Grid size={{ xs: 6, sm: 2, md: 2 }}>
                                            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                                                <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                <Typography variant="caption" color="text.secondary">
                                                    {convertToDateTime(fase.data_inizio_pianificata)?.toFormat("dd/MM/yyyy")}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                                → {convertToDateTime(fase.data_fine_pianificata)?.toFormat("dd/MM/yyyy")}
                                            </Typography>
                                        </Grid>

                                        {/* Avanzamento + Ostacoli */}
                                        <Grid size={{ xs: 12, sm: 12, md: 2 }}>
                                            <Stack spacing={0.5}>
                                                {/* Avanzamento */}
                                                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={avanzamento}
                                                        sx={{
                                                            flexGrow: 1,
                                                            height: 8,
                                                            borderRadius: 4,
                                                        }}
                                                        color={avanzamento >= 100 ? "success" : "primary"}
                                                    />
                                                    <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 36 }}>
                                                        {avanzamento.toFixed(0)}%
                                                    </Typography>
                                                </Stack>

                                                {/* Ostacoli */}
                                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                                                    {hasOstacoli && fase.ostacoli_pack?.map((ostacolo) => (
                                                        <Tooltip
                                                            key={ostacolo.id}
                                                            title={
                                                                `${ostacolo.tipo_display}: ${ostacolo.descrizione || "-"}` +
                                                                (ostacolo.data_prevista_risoluzione
                                                                    ? ` — Risoluzione prevista: ${DateTime.fromISO(ostacolo.data_prevista_risoluzione).toFormat("dd/MM/yyyy")}`
                                                                    : "")
                                                            }
                                                            arrow
                                                        >
                                                            <Chip
                                                                icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                                                                label={ostacolo.tipo_display}
                                                                color="warning"
                                                                size="small"
                                                                variant="filled"
                                                                sx={{ height: 24, fontSize: "0.75rem", cursor: "pointer" }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                    {fase.is_bloccata && !hasOstacoli && (
                                                        <Chip
                                                            label="Bloccata"
                                                            color="error"
                                                            size="small"
                                                        />
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {/* Empty state */}
                    {fasiFrazioniList.results.length === 0 &&
                        fasiFrazioniList.errorsStack.status !== ErrorStatusTypes.PENDING && (
                            <Card>
                                <CardContent>
                                    <Box sx={{ textAlign: "center", py: 4 }}>
                                        <PrecisionManufacturingIcon
                                            sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
                                        />
                                        <Typography variant="h6" color="text.secondary">
                                            Nessuna fase trovata per questo reparto
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                    {/* Paginazione */}
                    {fasiFrazioniList.results.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                justifyContent: "space-between",
                                alignItems: { xs: "stretch", sm: "center" },
                                gap: 2,
                                mt: 3,
                            }}
                        >
                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel>Record per pagina</InputLabel>
                                <Select
                                    value={filtri.numero_record || 20}
                                    label="Record per pagina"
                                    onChange={handleRecordsPerPageChange}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={20}>20</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>

                            <Pagination
                                count={fasiFrazioniList.num_pages}
                                page={filtri.page || 1}
                                onChange={handlePaginationChange}
                                color="primary"
                            />

                            <Typography variant="body2" color="text.secondary">
                                Totale: {fasiFrazioniList.count} fasi
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Dialog dettaglio fase */}
            <FaseFrazioneRepartoDetailDialog
                open={dialogOpen}
                fase={faseFrazioneCorrente}
                onClose={handleDialogClose}
            />

            {/* Loading */}
            <Backdrop
                open={fasiFrazioniList.errorsStack.status === ErrorStatusTypes.PENDING}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default FasiFrazioniRepartoPage;
